<template>
  <div class="max-w-4xl mx-auto px-4">
    <!-- 標題區塊 -->
    <div class="mb-8 text-center">
      <h1 class="text-4xl font-bold text-wood-800 mb-4 flex items-center justify-center">
        <div class="w-12 h-12 rounded-full yin-yang animate-pulse-soft mr-4"></div>
        {{ boxingTitle }}
      </h1>
      <p class="text-wood-600 text-lg mb-4">{{ boxingSubtitle }}</p>
      <div class="flex items-center justify-center space-x-6">
        <div class="bg-white/60 backdrop-blur-sm rounded-full px-6 py-2 shadow-md">
          <span class="text-wood-700 font-medium">共 {{ totalMoves }} 式</span>
        </div>
        <button @click="expandAll"
                class="px-6 py-2 bg-white/30 bg-gradient-to-r from-wood-500 to-wood-600 hover:from-wood-600 hover:to-wood-700 text-gray-700 rounded-full transition-all duration-300 transform hover:scale-105 shadow-lg">
          <i class="fas fa-expand mr-2"></i>
          {{ allExpanded ? '全部收合' : '全部展開' }}
        </button>
      </div>
    </div>

    <!-- 載入狀態 -->
    <div v-if="loading" class="flex justify-center items-center py-20">
      <div class="text-center">
        <div class="w-16 h-16 border-4 border-wood-300 border-t-wood-600 rounded-full animate-spin mx-auto mb-4"></div>
        <p class="text-wood-600 text-lg">載入拳譜資料中...</p>
      </div>
    </div>

    <!-- 錯誤狀態 -->
    <div v-else-if="error" class="text-center py-20">
      <div class="bg-red-100 border border-red-400 rounded-2xl p-8 max-w-md mx-auto">
        <i class="fas fa-exclamation-triangle text-red-500 text-4xl mb-4"></i>
        <p class="text-red-700 mb-4">{{ error }}</p>
        <button @click="loadBoxingChart"
                class="px-6 py-2 bg-red-500 hover:bg-red-600 text-white rounded-full transition-all duration-300">
          重新載入
        </button>
      </div>
    </div>

    <!-- 段落區塊 -->
    <div v-else class="space-y-8">
      <div v-for="(section, sectionIndex) in sections" :key="sectionIndex"
           class="bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg overflow-hidden border border-wood-200 post-shadow">

        <!-- 段落標題 - 只有多個段落時才顯示 -->
        <div v-if="sections.length > 1"
             class="bg-gradient-to-r from-wood-600 to-wood-700 text-white p-6 cursor-pointer select-none hover:from-wood-700 hover:to-wood-800 transition-all duration-300"
             @click="toggleSection(sectionIndex)">
          <div class="flex items-center justify-between">
            <div class="flex items-center space-x-4">
              <div class="w-12 h-12 bg-gradient-to-br from-neutral-200 to-neutral-400 backdrop-blur-sm rounded-full flex items-center justify-center border-2 border-wood-400/50 shadow-lg">
                <span class="text-2xl font-bold text-gray-600">{{ sectionIndex + 1 }}</span>
              </div>
              <div>
                <h2 class="text-3xl font-bold text-gray-600">{{ section.name }}</h2>
                <p class="text-wood-100 mt-2 text-white">
                  {{ section.subsections.length }}節 · {{ section.totalMoves }}式
                  <span v-if="hasMedia(section)" class="ml-2">
                    <i class="fas fa-photo-video text-amber-300"></i>
                    教學資源
                  </span>
                </p>
              </div>
            </div>
            <div class="transform transition-transform duration-300 text-white hover:text-gray-200"
                 :class="{ 'rotate-180': section.expanded }">
              <i class="fas fa-chevron-down text-2xl"></i>
            </div>
          </div>
        </div>

        <!-- 段落內容 -->
        <transition name="slide-down">
          <div v-show="sections.length === 1 || section.expanded" class="p-8">
            <div class="space-y-8">
              <div v-for="(subsection, subIndex) in section.subsections" :key="subIndex">

                <!-- 小節標題 -->
                <div v-if="subsection.name" class="flex items-center space-x-4 mb-6">
                  <div class="w-10 h-10 bg-gradient-to-r from-amber-500 to-orange-500 rounded-xl flex items-center justify-center shadow-lg">
                    <span class="text-wood font-bold">{{ subIndex + 1 }}</span>
                  </div>
                  <h3 class="text-2xl font-semibold text-wood-800">{{ subsection.name }}</h3>
                  <div class="flex-1 h-px bg-gradient-to-r from-wood-300 to-transparent"></div>
                  <span class="text-wood-600 text-sm">{{ subsection.moves.length }}式</span>
                  <!-- 小節媒體圖示 -->
                  <button v-if="hasMedia(subsection)"
                          @click.stop="showMediaModal(subsection.media, subsection.name)"
                          class="px-3 py-1 bg-amber-100 hover:bg-amber-200 text-amber-700 rounded-full transition-all duration-200 flex items-center space-x-2">
                    <i class="fas fa-photo-video"></i>
                    <span class="text-xs font-medium">教學資源</span>
                  </button>
                </div>

                <!-- 招式網格 -->
                <div class="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                  <div v-for="move in subsection.moves" :key="move.number"
                       class="bg-white/70 rounded-xl p-5 shadow-md hover:shadow-xl border border-wood-200/50 cursor-pointer transition-all duration-300 hover:transform hover:scale-105"
                       @click="toggleMove(sectionIndex, subIndex, move.number)">

                    <!-- 招式標題 -->
                    <div class="flex items-start justify-between mb-4">
                      <div class="flex items-center space-x-3">
                        <div class="w-10 h-10 bg-gradient-to-r from-wood-500 to-wood-600 rounded-xl flex items-center justify-center shadow-md">
                          <span class="text-white font-bold text-sm">{{ move.number }}</span>
                        </div>
                        <div class="flex-1">
                          <div class="flex items-center space-x-2">
                            <h4 class="font-bold text-wood-800 text-lg leading-tight">{{ move.name }}</h4>
                            <!-- 招式媒體圖示 -->
                            <button v-if="hasMedia(move)"
                                    @click.stop="showMediaModal(move.media, move.name)"
                                    class="text-amber-500 hover:text-amber-600 transition-colors">
                              <i class="fas fa-photo-video text-sm"></i>
                            </button>
                          </div>
                          <div v-if="move.reference" class="mt-1">
                            <span class="text-xs bg-amber-100 text-amber-700 px-2 py-1 rounded-full">
                              {{ move.reference }}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div class="text-wood-400 flex-shrink-0">
                        <i class="fas fa-chevron-down transform transition-transform duration-200"
                           :class="{ 'rotate-180': move.expanded }"></i>
                      </div>
                    </div>

                    <!-- 招式詳細內容 -->
                    <transition name="slide-down">
                      <div v-show="move.expanded" class="border-t border-wood-200 pt-4">
                        <div v-if="move.details && move.details.length > 0" class="space-y-2">
                          <div v-for="(detail, detailIndex) in move.details" :key="detailIndex"
                               class="flex items-start space-x-3 text-sm">
                            <div class="w-2 h-2 bg-wood-400 rounded-full mt-2 flex-shrink-0"></div>
                            <span class="text-wood-700 leading-relaxed">{{ detail }}</span>
                          </div>
                        </div>
                        <div v-else class="text-wood-500 text-sm italic">
                          詳細動作說明待補充...
                        </div>
                      </div>
                    </transition>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </transition>
      </div>
    </div>

    <!-- 返回按鈕 -->
    <div class="mt-12 text-center">
      <button @click="goBack"
              class="px-8 py-3 bg-gradient-to-r from-wood-200 to-wood-500 hover:from-wood-600 hover:to-wood-700 text-gray-600 rounded-full transition-all duration-300 transform hover:scale-105 shadow-lg">
        <i class="fas fa-arrow-left mr-2"></i>
        返回教材頁面
      </button>
    </div>

    <!-- 媒體資源彈窗 -->
    <transition name="fade">
      <div v-if="showModal"
           @click="closeMediaModal"
           class="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
        <div @click.stop
             class="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
          <!-- 彈窗標題 -->
          <div class="sticky top-0 bg-gradient-to-r from-wood-600 to-wood-700 text-white p-6 rounded-t-2xl flex items-center justify-between">
            <div class="flex items-center space-x-3">
              <i class="fas fa-photo-video text-2xl"></i>
              <h3 class="text-2xl font-bold">{{ modalTitle }} - 教學資源</h3>
            </div>
            <button @click="closeMediaModal"
                    class="text-white hover:text-gray-200 transition-colors">
              <i class="fas fa-times text-2xl"></i>
            </button>
          </div>

          <!-- 彈窗內容 -->
          <div class="p-6 space-y-6">
            <!-- 影片列表 -->
            <div v-if="currentMedia?.videos?.length > 0">
              <h4 class="text-xl font-semibold text-wood-800 mb-4 flex items-center">
                <i class="fas fa-video mr-2 text-red-500"></i>
                教學影片
              </h4>
              <div class="space-y-4">
                <div v-for="video in currentMedia.videos" :key="video.id"
                     class="bg-gradient-to-r from-gray-50 to-gray-100 rounded-xl p-4 hover:shadow-lg transition-all duration-300 cursor-pointer"
                     @click="openVideoLightbox(video)">
                  <div class="flex items-start space-x-4">
                    <div class="w-12 h-12 bg-red-500 rounded-xl flex items-center justify-center flex-shrink-0">
                      <i class="fas fa-play text-white"></i>
                    </div>
                    <div class="flex-1">
                      <h5 class="font-semibold text-wood-800 mb-2">{{ video.title }}</h5>
                      <div class="flex flex-wrap gap-2 text-sm text-gray-600 mb-3">
                        <span v-if="video.duration" class="flex items-center">
                          <i class="far fa-clock mr-1"></i>
                          {{ video.duration }}
                        </span>
                        <span v-if="video.instructor" class="flex items-center">
                          <i class="fas fa-user mr-1"></i>
                          {{ video.instructor }}
                        </span>
                        <span v-if="video.uploadDate" class="flex items-center">
                          <i class="far fa-calendar mr-1"></i>
                          {{ video.uploadDate }}
                        </span>
                      </div>
                      <div class="inline-flex items-center px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors">
                        <i class="fas fa-play mr-2"></i>
                        全螢幕觀看
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- 圖片列表 -->
            <div v-if="currentMedia?.images?.length > 0">
              <h4 class="text-xl font-semibold text-wood-800 mb-4 flex items-center">
                <i class="fas fa-images mr-2 text-blue-500"></i>
                教學圖片
              </h4>
              <div class="grid grid-cols-2 md:grid-cols-3 gap-4">
                <div v-for="(image, index) in currentMedia.images" :key="image.id"
                     class="group relative rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 cursor-pointer"
                     @click="openImageLightbox(index, currentMedia.images)">
                  <img :src="image.url"
                       :alt="image.caption"
                       class="w-full h-48 object-cover">
                  <div class="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end">
                    <p class="text-white p-3 text-sm">{{ image.caption }}</p>
                  </div>
                  <div v-if="image.order"
                       class="absolute top-2 right-2 w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center text-white font-bold text-sm">
                    {{ image.order }}
                  </div>
                  <!-- 放大提示圖示 -->
                  <div class="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <div class="bg-black/60 text-white p-3 rounded-full">
                      <i class="fas fa-search-plus text-xl"></i>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- 音訊列表 -->
            <div v-if="currentMedia?.audio?.length > 0">
              <h4 class="text-xl font-semibold text-wood-800 mb-4 flex items-center">
                <i class="fas fa-headphones mr-2 text-purple-500"></i>
                音訊教材
              </h4>
              <div class="space-y-3">
                <div v-for="audio in currentMedia.audio" :key="audio.id"
                     class="bg-gradient-to-r from-purple-50 to-purple-100 rounded-xl p-4">
                  <div class="flex items-center space-x-3">
                    <i class="fas fa-music text-purple-500 text-xl"></i>
                    <span class="flex-1 font-medium text-wood-800">{{ audio.title }}</span>
                    <audio :src="audio.url" controls class="max-w-xs"></audio>
                  </div>
                </div>
              </div>
            </div>

            <!-- 無媒體資源提示 -->
            <div v-if="!hasAnyMedia(currentMedia)"
                 class="text-center py-12 text-gray-500">
              <i class="fas fa-inbox text-4xl mb-4"></i>
              <p>暫無教學資源</p>
            </div>
          </div>
        </div>
      </div>
    </transition>

    <!-- 圖片燈箱 -->
    <Teleport to="body">
      <div
        v-if="isImageLightboxOpen"
        class="fixed inset-0 z-[9999] flex items-center justify-center"
        @click="closeImageLightbox"
      >
        <!-- 半透明深色背景 -->
        <div
          class="absolute inset-0 bg-black transition-opacity duration-300"
          :class="isImageLightboxAnimating ? 'opacity-0' : 'opacity-90'"
        ></div>

        <!-- 燈箱內容 -->
        <div
          class="relative max-w-[95vw] max-h-[95vh] flex items-center justify-center transform transition-all duration-300"
          :class="isImageLightboxAnimating ? 'scale-75 opacity-0' : 'scale-100 opacity-100'"
          @click.stop
          @touchstart="handleTouchStart"
          @touchend="handleTouchEnd"
        >
          <!-- 主圖片 -->
          <img
            :src="lightboxImages[lightboxImageIndex]?.url"
            :key="`lightbox-${lightboxImageIndex}`"
            class="lightbox-image max-w-[90vw] max-h-[90vh] w-auto h-auto object-contain rounded-lg shadow-2xl"
            @click.stop
          />

          <!-- 關閉按鈕 -->
          <button
            @click="closeImageLightbox"
            class="lightbox-button absolute top-4 right-4 bg-black/60 text-white rounded-full w-12 h-12 flex items-center justify-center hover:bg-black/80"
          >
            <i class="fas fa-times text-lg"></i>
          </button>

          <!-- 圖片說明 -->
          <div
            v-if="lightboxImages[lightboxImageIndex]?.caption"
            class="absolute bottom-6 left-1/2 transform -translate-x-1/2 bg-black/60 text-white px-4 py-2 rounded-lg max-w-md text-center"
          >
            {{ lightboxImages[lightboxImageIndex].caption }}
          </div>

          <!-- 圖片指示器 (多張圖片時) -->
          <div
            v-if="lightboxImages.length > 1"
            class="absolute bottom-20 left-1/2 transform -translate-x-1/2 flex space-x-2"
          >
            <button
              v-for="(_, index) in lightboxImages"
              :key="index"
              @click="lightboxImageIndex = index"
              class="w-3 h-3 rounded-full transition-all duration-200 hover:scale-125"
              :class="lightboxImageIndex === index ? 'bg-white shadow-lg' : 'bg-white/50 hover:bg-white/75'"
            ></button>
          </div>

          <!-- 圖片計數器 (多張圖片時) -->
          <div
            v-if="lightboxImages.length > 1"
            class="absolute top-4 left-1/2 transform -translate-x-1/2 bg-black/60 text-white px-4 py-2 rounded-full text-sm font-medium"
          >
            {{ lightboxImageIndex + 1 }} / {{ lightboxImages.length }}
          </div>
        </div>

        <!-- 左右導航按鈕 (多張圖片時) -->
        <template v-if="lightboxImages.length > 1">
          <button
            @click.stop="previousLightboxImage"
            class="lightbox-button fixed left-8 top-1/2 transform -translate-y-1/2 bg-black/60 text-white rounded-full w-16 h-16 flex items-center justify-center hover:bg-black/80 z-[10001]"
          >
            <i class="fas fa-chevron-left text-xl"></i>
          </button>
          <button
            @click.stop="nextLightboxImage"
            class="lightbox-button fixed right-8 top-1/2 transform -translate-y-1/2 bg-black/60 text-white rounded-full w-16 h-16 flex items-center justify-center hover:bg-black/80 z-[10001]"
          >
            <i class="fas fa-chevron-right text-xl"></i>
          </button>
        </template>
      </div>
    </Teleport>

    <!-- YouTube 影片燈箱 -->
    <Teleport to="body">
      <div
        v-if="isVideoLightboxOpen"
        class="fixed inset-0 z-[9999] flex items-center justify-center"
        @click="closeVideoLightbox"
      >
        <!-- 半透明深色背景 -->
        <div
          class="absolute inset-0 bg-black transition-opacity duration-300"
          :class="isVideoLightboxAnimating ? 'opacity-0' : 'opacity-95'"
        ></div>

        <!-- YouTube 燈箱內容 -->
        <div
          class="relative max-w-[95vw] max-h-[95vh] w-full h-full flex items-center justify-center transform transition-all duration-300"
          :class="isVideoLightboxAnimating ? 'scale-75 opacity-0' : 'scale-100 opacity-100'"
          @click.stop
        >
          <!-- YouTube 播放器容器 -->
          <div
            class="lightbox-youtube max-w-[90vw] max-h-[90vh] w-auto h-auto rounded-lg shadow-2xl overflow-hidden bg-black"
            style="aspect-ratio: 16/9; width: min(90vw, calc(90vh * 16/9));"
          >
            <iframe
              v-if="currentVideo"
              width="100%"
              height="100%"
              :src="getYoutubeEmbedUrl(currentVideo.url) + '&autoplay=1'"
              frameborder="0"
              allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
              allowfullscreen>
            </iframe>
          </div>

          <!-- 關閉按鈕 -->
          <button
            @click="closeVideoLightbox"
            class="lightbox-button absolute top-4 right-4 bg-black/60 text-white rounded-full w-12 h-12 flex items-center justify-center hover:bg-black/80 z-10"
          >
            <i class="fas fa-times text-lg"></i>
          </button>

          <!-- 影片資訊 -->
          <div v-if="currentVideo?.title" class="absolute bottom-4 left-4 bg-black/60 text-white px-4 py-2 rounded-lg max-w-md">
            <div class="flex items-center space-x-2 text-sm">
              <i class="fas fa-play-circle"></i>
              <span>{{ currentVideo.instructor || '教學影片' }}</span>
            </div>
            <div class="text-sm opacity-90 mt-1">{{ currentVideo.title }}</div>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch, nextTick } from 'vue'
import axios from 'axios'

// Props
const props = defineProps({
  jsonPath: {
    type: String,
    default: '/楊家老架太極拳108式.json'
  }
})

// 響應式資料
const sections = ref([])
const boxingTitle = ref('楊氏太極拳108式')
const boxingSubtitle = ref('三段四節傳統拳譜')
const loading = ref(false)
const error = ref(null)
const showModal = ref(false)
const currentMedia = ref(null)
const modalTitle = ref('')

// 燈箱狀態管理
const isImageLightboxOpen = ref(false)
const isImageLightboxAnimating = ref(false)
const lightboxImages = ref([])
const lightboxImageIndex = ref(0)

// 影片燈箱狀態管理
const isVideoLightboxOpen = ref(false)
const isVideoLightboxAnimating = ref(false)
const currentVideo = ref(null)

// 觸控手勢相關
const touchStartX = ref(0)
const touchEndX = ref(0)

// 計算屬性
const totalMoves = computed(() => {
  return sections.value.reduce((total, section) => total + section.totalMoves, 0)
})

const allExpanded = computed(() => {
  return sections.value.every(section => section.expanded)
})

// 方法
const toggleSection = (sectionIndex) => {
  sections.value[sectionIndex].expanded = !sections.value[sectionIndex].expanded
}

const toggleMove = (sectionIndex, subIndex, moveNumber) => {
  const move = sections.value[sectionIndex].subsections[subIndex].moves.find(m => m.number === moveNumber)
  if (move) {
    move.expanded = !move.expanded
  }
}

const expandAll = () => {
  const shouldExpand = !allExpanded.value
  sections.value.forEach(section => {
    section.expanded = shouldExpand
    section.subsections.forEach(subsection => {
      subsection.moves.forEach(move => {
        move.expanded = shouldExpand
      })
    })
  })
}

const goBack = () => {
  // 回到教材頁面
  window.location.href = '/materials'
}

// 檢查是否有媒體資源
const hasMedia = (item) => {
  if (!item?.media) return false
  return (item.media.videos?.length > 0) ||
         (item.media.images?.length > 0) ||
         (item.media.audio?.length > 0)
}

// 檢查媒體物件是否有任何內容
const hasAnyMedia = (media) => {
  if (!media) return false
  return (media.videos?.length > 0) ||
         (media.images?.length > 0) ||
         (media.audio?.length > 0)
}

// 顯示媒體彈窗
const showMediaModal = (media, title) => {
  currentMedia.value = media
  modalTitle.value = title
  showModal.value = true
  // 防止背景滾動
  document.body.style.overflow = 'hidden'
}

// 關閉媒體彈窗
const closeMediaModal = () => {
  showModal.value = false
  currentMedia.value = null
  modalTitle.value = ''
  // 恢復背景滾動
  document.body.style.overflow = ''
}

// 圖片燈箱相關方法
const openImageLightbox = (index, images) => {
  lightboxImages.value = images
  lightboxImageIndex.value = index
  isImageLightboxOpen.value = true
  isImageLightboxAnimating.value = true
  document.body.style.overflow = 'hidden'

  setTimeout(() => {
    isImageLightboxAnimating.value = false
  }, 100)
}

const closeImageLightbox = () => {
  isImageLightboxAnimating.value = true

  setTimeout(() => {
    isImageLightboxOpen.value = false
    isImageLightboxAnimating.value = false
    lightboxImages.value = []
    document.body.style.overflow = ''
  }, 300)
}

const nextLightboxImage = () => {
  if (lightboxImages.value.length > 1) {
    lightboxImageIndex.value = (lightboxImageIndex.value + 1) % lightboxImages.value.length
  }
}

const previousLightboxImage = () => {
  if (lightboxImages.value.length > 1) {
    lightboxImageIndex.value = lightboxImageIndex.value === 0
      ? lightboxImages.value.length - 1
      : lightboxImageIndex.value - 1
  }
}

// 影片燈箱相關方法
const getYoutubeEmbedUrl = (url) => {
  // 將 YouTube 網址轉換為嵌入格式
  if (!url) return ''

  // 如果已經是嵌入網址，直接返回
  if (url.includes('youtube.com/embed/')) {
    return url
  }

  // 從各種 YouTube 網址格式提取影片 ID
  let videoId = ''

  if (url.includes('youtube.com/watch?v=')) {
    videoId = url.split('watch?v=')[1].split('&')[0]
  } else if (url.includes('youtu.be/')) {
    videoId = url.split('youtu.be/')[1].split('?')[0]
  }

  return videoId ? `https://www.youtube.com/embed/${videoId}?rel=0` : url
}

const openVideoLightbox = (video) => {
  currentVideo.value = video
  isVideoLightboxOpen.value = true
  isVideoLightboxAnimating.value = true
  document.body.style.overflow = 'hidden'

  setTimeout(() => {
    isVideoLightboxAnimating.value = false
  }, 100)
}

const closeVideoLightbox = () => {
  isVideoLightboxAnimating.value = true

  setTimeout(() => {
    isVideoLightboxOpen.value = false
    isVideoLightboxAnimating.value = false
    currentVideo.value = null
    document.body.style.overflow = ''
  }, 300)
}

// 鍵盤事件處理
const handleKeydown = (event) => {
  if (!isImageLightboxOpen.value && !isVideoLightboxOpen.value) return

  switch (event.key) {
    case 'Escape':
      if (isImageLightboxOpen.value) {
        closeImageLightbox()
      } else if (isVideoLightboxOpen.value) {
        closeVideoLightbox()
      }
      break
    case 'ArrowLeft':
      if (isImageLightboxOpen.value) {
        previousLightboxImage()
      }
      break
    case 'ArrowRight':
      if (isImageLightboxOpen.value) {
        nextLightboxImage()
      }
      break
  }
}

// 觸控手勢處理
const handleTouchStart = (event) => {
  touchStartX.value = event.touches[0].clientX
}

const handleTouchEnd = (event) => {
  touchEndX.value = event.changedTouches[0].clientX
  const diff = touchStartX.value - touchEndX.value
  const minSwipeDistance = 50

  if (Math.abs(diff) > minSwipeDistance) {
    if (diff > 0) {
      nextLightboxImage() // 向左滑動，下一張
    } else {
      previousLightboxImage() // 向右滑動，上一張
    }
  }
}

// 定義發送給父組件的事件
const emit = defineEmits(['goBack'])

// 載入拳譜資料
const loadBoxingChart = async () => {
  loading.value = true
  error.value = null

  try {
    // 使用 props 傳入的 JSON 路徑
    const response = await axios.get(props.jsonPath)
    const data = response.data

    // 載入標題資訊
    if (data.title) {
      boxingTitle.value = data.title
      // 更新網頁標題
      document.title = `${data.title} - 雲林縣楊家老架太極武藝協會`
    }
    if (data.subtitle) {
      boxingSubtitle.value = data.subtitle
    }

    // 載入拳譜段落資料
    sections.value = data.sections || data
    console.log('拳譜資料載入成功:', data)
  } catch (err) {
    console.error('載入拳譜資料失敗:', err)
    error.value = `載入拳譜資料失敗: ${err.message}`
  } finally {
    loading.value = false
  }
}

// 監聽 jsonPath 變化
watch(() => props.jsonPath, (newPath) => {
  console.log('JSON 路徑已變更:', newPath)
  if (newPath) {
    loadBoxingChart()
  }
}, { immediate: true })

// 組件掛載
onMounted(() => {
  console.log('拳譜組件已載入, JSON 路徑:', props.jsonPath)
  window.addEventListener('keydown', handleKeydown)
})

// 組件卸載
onUnmounted(() => {
  window.removeEventListener('keydown', handleKeydown)
  document.body.style.overflow = '' // 確保恢復滾動
  // 恢復原來的網頁標題
  document.title = '雲林縣楊家老架太極武藝協會'
})
</script>

<style scoped>
/* 動畫效果 */
.slide-down-enter-active,
.slide-down-leave-active {
  transition: all 0.3s ease;
}

.slide-down-enter-from {
  opacity: 0;
  transform: translateY(-10px);
}

.slide-down-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

/* 彈窗淡入淡出動畫 */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* 陰陽太極圖 */
.yin-yang {
  background: conic-gradient(from 0deg, #000 0deg 180deg, #fff 180deg 360deg);
  position: relative;
}

.yin-yang::before {
  content: '';
  position: absolute;
  top: 25%;
  left: 50%;
  width: 50%;
  height: 50%;
  background: #fff;
  border-radius: 50%;
  transform: translateX(-50%);
}

.yin-yang::after {
  content: '';
  position: absolute;
  top: 75%;
  left: 50%;
  width: 50%;
  height: 50%;
  background: #000;
  border-radius: 50%;
  transform: translateX(-50%);
}

/* 燈箱特效 */
.lightbox-image {
  transition: all 0.5s cubic-bezier(0.25, 0.8, 0.25, 1);
  will-change: transform, opacity;
  backface-visibility: hidden;
  transform: translateZ(0);
}

/* 燈箱按鈕效果 */
.lightbox-button {
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  backdrop-filter: blur(10px);
}

.lightbox-button:hover {
  transform: scale(1.1);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.4);
}

.lightbox-button:active {
  transform: scale(0.95);
}

/* YouTube 燈箱特效 */
.lightbox-youtube {
  transition: all 0.5s cubic-bezier(0.25, 0.8, 0.25, 1);
  will-change: transform, opacity;
  min-width: 320px;
  min-height: 180px;
}

/* 木質色彩系統（與其他組件一致）*/
.text-wood-800 { color: #4a3f35; }
.text-wood-700 { color: #6d5a47; }
.text-wood-600 { color: #8b7355; }
.bg-wood-600 { background-color: #8b7355; }
.bg-wood-700 { background-color: #6d5a47; }
</style>
